import { useEffect, useState } from 'react';
import { useContentSelector } from '@wajeez-web/stores/content';
import { Server } from '@wajeez/wajeez.microservices';
import { useGetCollectionComponentByIdMultilingual } from '@wajeez-web/components/helpers';
import { Languages } from '@wajeez-web/core/utils';

import {
  InputFieldWithSelectorOptions,
  InputFieldWithSelectorOptionsAndValues,
} from './ComponentFormBuilder';
import { CollectionComponentFormBuilder } from './CollectionComponentFormBuilder';

export interface CollectionComponentBody {
  referenceName: string | undefined;
  eventName: string | undefined;
  pageNumber: number | undefined;
  pageSize: number | undefined;
  profiles: {
    fields: InputFieldWithSelectorOptionsAndValues[];
    languageId: Languages;
  }[];
  selectors: {
    defaultCritiria?: string | undefined;
    factor?: Server.SelectFactor;
    defaultRelationLength?: number | undefined;
    defaultTypeId?: number | undefined;
    defaultEntityId?: string | undefined;
  }[];
  sorters: Server.CollectionComponentContentEntitySorter[];
}

interface CollectionComponentEditFormProps {
  inputFieldsWithSelectorOptions: InputFieldWithSelectorOptions[] | undefined;
  onSubmit: () => void;
  onCancel?: () => void;
}

export const CollectionComponentEditForm = ({
  inputFieldsWithSelectorOptions,
  onSubmit,
  onCancel,
}: CollectionComponentEditFormProps) => {
  const [
    inputFieldsWithSelectorOptionsAndValues,
    setInputFieldsWithSelectorOptionsAndValues,
  ] = useState<CollectionComponentBody | undefined>();

  const { data: collectionComponentDetailsMultilingualData } =
    useGetCollectionComponentByIdMultilingual();

  useEffect(() => {
    if (
      collectionComponentDetailsMultilingualData &&
      inputFieldsWithSelectorOptions
    ) {
      const fieldsWithValues: CollectionComponentBody = {
        referenceName: collectionComponentDetailsMultilingualData.referenceName,
        eventName: collectionComponentDetailsMultilingualData.eventName,
        pageNumber: collectionComponentDetailsMultilingualData.pageNumber,
        pageSize: collectionComponentDetailsMultilingualData.pageSize,
        profiles:
          collectionComponentDetailsMultilingualData.profiles?.map((p) => {
            return {
              languageId: p.languageId!,
              fields:
                inputFieldsWithSelectorOptions.map((i) => {
                  let value: string | undefined;

                  const parameter = p.parameters?.find(
                    (p) => p.name === i.name
                  );

                  if (i.fieldType === Server.FieldType.PredefinedComponent) {
                    value = parameter?.type;
                  } else if (i.fieldType === Server.FieldType.MappedField) {
                    value = JSON.stringify(
                      parameter?.mappingOptions?.map((o) => {
                        return o.entityTypeFieldId!;
                      })
                    );
                  } else if (
                    i.fieldType === Server.FieldType.RequiredComponent
                  ) {
                    value = parameter?.id;
                  } else {
                    value = parameter?.value;
                  }

                  return {
                    ...i,
                    value,
                  };
                }) || [],
            };
          }) || [],
        selectors:
          collectionComponentDetailsMultilingualData.selectors?.map((s) => {
            return {
              ...s,
              defaultCritiria: s.defaultCriteria,
            };
          }) || [],
        sorters: collectionComponentDetailsMultilingualData.sorters || [],
      };

      setInputFieldsWithSelectorOptionsAndValues(fieldsWithValues);
    }
  }, [
    collectionComponentDetailsMultilingualData,
    inputFieldsWithSelectorOptions,
  ]);

  return (
    <div className="w-full mx-auto">
      {inputFieldsWithSelectorOptionsAndValues && (
        <CollectionComponentFormBuilder
          type="Edit"
          dynamicFields={
            inputFieldsWithSelectorOptionsAndValues.profiles[0].fields
          }
          profiles={inputFieldsWithSelectorOptionsAndValues.profiles}
          onSubmit={onSubmit}
          referenceName={inputFieldsWithSelectorOptionsAndValues.referenceName}
          eventName={inputFieldsWithSelectorOptionsAndValues.referenceName}
          pageNumber={inputFieldsWithSelectorOptionsAndValues.pageNumber}
          pageSize={inputFieldsWithSelectorOptionsAndValues.pageSize}
          selectors={inputFieldsWithSelectorOptionsAndValues.selectors}
          sorters={inputFieldsWithSelectorOptionsAndValues.sorters}
          onCancel={onCancel}
        />
      )}
    </div>
  );
};
